import { c as _c } from "react/compiler-runtime";
import { type SortingState } from '@tanstack/react-table';
import { useEffect, useRef, useState } from 'react';
import type { ChartCardFetchData, ChartCardFetchQuery, ChartCardFetchResult, ChartDataRow } from '../types';
import type { TotalsAndRangeData } from '../internalTypes';
import type { TablePaginationState } from '../components/table/types';
import { useDeepCompareMemoize } from './generics';
const DEFAULT_PAGE_SIZE = 10;
export interface ChartCardFetchQueryState<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  data: DataRow[];
  totalRows: number;
  totals: TotalsAndRangeData<DataKey> | undefined;
  isLoading: boolean;
  isError: boolean;
  pagination: TablePaginationState;
  sorting: SortingState;
  setPagination: (updater: TablePaginationState | ((prev: TablePaginationState) => TablePaginationState)) => void;
  setSorting: (updater: SortingState | ((prev: SortingState) => SortingState)) => void;
  resetPageIndex: () => void;
}

/**
 * Owns ChartCard `fetchData` state and calls it when the query changes.
 * Used for charts and tables. When `usePagination` is true (tables only), page index/size
 * are included and drive refetches; otherwise fetches the full set (plus sort/reset).
 * Exposes `isLoading` / `isError` from the in-flight promise for skeleton and error UI.
 * Also reports loading when enabled with no result yet (before the fetch effect runs), so
 * callers do not treat empty data as ready for a frame after `enabled` flips true.
 * Keeps previous data in state while the next response loads (keepPreviousData-style) so the
 * table can render per-row skeletons and keep pagination interactive. Rapid page changes are
 * safe: each click updates page state immediately and only the latest in-flight request is
 * applied (stale responses are ignored via request id).
 *
 * Pass `externalPagination` / `externalSorting` to mirror another query (e.g. dataCompare
 * following the primary fetch's page/sort when server fetch pagination is on).
 */
export const useChartCardFetchQuery = t0 => {
  const $ = _c(39);
  const {
    enabled,
    fetchData,
    usePagination,
    initialPageSize: t1,
    initialSorting,
    resetKey,
    dimensions,
    externalPagination,
    externalSorting
  } = t0;
  const initialPageSize = t1 === undefined ? DEFAULT_PAGE_SIZE : t1;
  const isExternallyControlled = externalPagination != null || externalSorting != null;
  const stableDimensions = useDeepCompareMemoize(dimensions);
  let t2;
  if ($[0] !== initialPageSize) {
    t2 = {
      pageIndex: 0,
      pageSize: initialPageSize
    };
    $[0] = initialPageSize;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [pagination, setPagination] = useState(t2);
  const [sorting, setSorting] = useState(initialSorting);
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const [isError, setIsError] = useState(false);
  const requestIdRef = useRef(0);
  const hasAppliedInitialSortingRef = useRef(false);
  const [prevResetKey, setPrevResetKey] = useState(resetKey);
  let t3;
  if ($[2] !== initialSorting) {
    t3 = () => JSON.stringify(initialSorting);
    $[2] = initialSorting;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [prevSortingSerialized, setPrevSortingSerialized] = useState(t3);
  const [prevDimensions, setPrevDimensions] = useState(stableDimensions);
  const effectivePagination = externalPagination ?? pagination;
  const effectiveSorting = externalSorting ?? sorting;
  const stableSorting = useDeepCompareMemoize(effectiveSorting);
  const isAwaitingFirstResult = enabled && fetchData != null && result === null && !isError;
  let t4;
  let t5;
  if ($[4] !== enabled || $[5] !== initialSorting || $[6] !== isExternallyControlled) {
    t4 = () => {
      if (!enabled || isExternallyControlled || hasAppliedInitialSortingRef.current || initialSorting.length === 0) {
        return;
      }
      hasAppliedInitialSortingRef.current = true;
      setSorting(prev => JSON.stringify(prev) === JSON.stringify(initialSorting) ? prev : initialSorting);
    };
    t5 = [enabled, initialSorting, isExternallyControlled];
    $[4] = enabled;
    $[5] = initialSorting;
    $[6] = isExternallyControlled;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== isExternallyControlled) {
    t6 = () => {
      if (isExternallyControlled) {
        return;
      }
      setPagination(_temp);
    };
    $[9] = isExternallyControlled;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const resetPageIndex = t6;
  if (enabled && usePagination && !isExternallyControlled) {
    if (prevResetKey !== resetKey) {
      setPrevResetKey(resetKey);
      if (pagination.pageIndex !== 0) {
        setPagination(_temp2);
      }
    }
    const sortingSerialized = JSON.stringify(sorting);
    if (prevSortingSerialized !== sortingSerialized) {
      setPrevSortingSerialized(sortingSerialized);
      if (pagination.pageIndex !== 0) {
        setPagination(_temp3);
      }
    }
    if (prevDimensions !== stableDimensions) {
      setPrevDimensions(stableDimensions);
      if (pagination.pageIndex !== 0) {
        setPagination(_temp4);
      }
    }
  }
  let t7;
  if ($[11] !== effectivePagination.pageIndex || $[12] !== effectivePagination.pageSize || $[13] !== enabled || $[14] !== fetchData || $[15] !== stableDimensions || $[16] !== stableSorting || $[17] !== usePagination) {
    t7 = () => {
      if (!enabled || fetchData == null) {
        setIsLoading(false);
        return;
      }
      const dimensionsQuery = stableDimensions != null ? {
        dimensions: stableDimensions
      } : {};
      const query = usePagination ? {
        pageIndex: effectivePagination.pageIndex,
        pageSize: effectivePagination.pageSize,
        sorting: stableSorting,
        ...dimensionsQuery
      } : {
        sorting: stableSorting,
        ...dimensionsQuery
      };
      const requestId = requestIdRef.current = requestIdRef.current + 1;
      setIsLoading(true);
      setIsError(false);
      fetchData(query).then(fetchResult => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setResult(fetchResult);
        setIsLoading(false);
        setIsError(false);
      }).catch(() => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setIsLoading(false);
        setIsError(true);
      });
    };
    $[11] = effectivePagination.pageIndex;
    $[12] = effectivePagination.pageSize;
    $[13] = enabled;
    $[14] = fetchData;
    $[15] = stableDimensions;
    $[16] = stableSorting;
    $[17] = usePagination;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== effectivePagination.pageIndex || $[20] !== effectivePagination.pageSize || $[21] !== enabled || $[22] !== fetchData || $[23] !== resetKey || $[24] !== stableDimensions || $[25] !== stableSorting || $[26] !== usePagination) {
    t8 = [enabled, fetchData, usePagination, effectivePagination.pageIndex, effectivePagination.pageSize, stableSorting, resetKey, stableDimensions];
    $[19] = effectivePagination.pageIndex;
    $[20] = effectivePagination.pageSize;
    $[21] = enabled;
    $[22] = fetchData;
    $[23] = resetKey;
    $[24] = stableDimensions;
    $[25] = stableSorting;
    $[26] = usePagination;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  useEffect(t7, t8);
  let t9;
  if ($[28] !== result?.data) {
    t9 = result?.data ?? [];
    $[28] = result?.data;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const t10 = result?.totalRows ?? 0;
  const t11 = result?.totals;
  const t12 = isLoading || isAwaitingFirstResult;
  let t13;
  if ($[30] !== effectivePagination || $[31] !== effectiveSorting || $[32] !== isError || $[33] !== resetPageIndex || $[34] !== t10 || $[35] !== t11 || $[36] !== t12 || $[37] !== t9) {
    t13 = {
      data: t9,
      totalRows: t10,
      totals: t11,
      isLoading: t12,
      isError,
      pagination: effectivePagination,
      sorting: effectiveSorting,
      setPagination,
      setSorting,
      resetPageIndex
    };
    $[30] = effectivePagination;
    $[31] = effectiveSorting;
    $[32] = isError;
    $[33] = resetPageIndex;
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
    $[37] = t9;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  return t13;
};
export const DEFAULT_TABLE_PAGE_SIZE = DEFAULT_PAGE_SIZE;
function _temp(prev_0) {
  return prev_0.pageIndex === 0 ? prev_0 : {
    ...prev_0,
    pageIndex: 0
  };
}
function _temp2(prev_1) {
  return {
    ...prev_1,
    pageIndex: 0
  };
}
function _temp3(prev_2) {
  return {
    ...prev_2,
    pageIndex: 0
  };
}
function _temp4(prev_3) {
  return {
    ...prev_3,
    pageIndex: 0
  };
}